import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { A11y, Autoplay, EffectFade, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, IconButton, ResponsiveValue } from '@chakra-ui/react';
import { ChevronRightIcon } from '@udacity/chakra-uds-icons';

import { ChevronLeftIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useDelayedSwiperAutoplay } from '~/features/promotions/hooks/use-delayed-swiper-autoplay';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export function FadeCardCarousel<
  T extends {
    key: string;
  },
>({
  autoplayStartDelayMs = 0,
  autoplayStartOnViewport = false,
  isAutoplayPaused = false,
  items,
  minHeight,
  renderCard,
  isReady = true,
  onSlideView,
}: {
  /** Ms to wait after the carousel is ready (and in viewport, if required) before autoplay starts. */
  autoplayStartDelayMs?: number;
  /** When true, the delay countdown begins only once the carousel intersects the viewport. */
  autoplayStartOnViewport?: boolean;
  /** Stops autoplay (and clears a pending delayed start) until false. Does not remount the swiper. */
  isAutoplayPaused?: boolean;
  items: T[];
  /**
   * Reserved height including pagination (`paddingBottom: 48px` on `.swiper`) so the section
   * below does not shift. Homepage Impact uses `432px` / `394px`; LP cards are ~441 / ~406 plus
   * pagination.
   */
  minHeight: ResponsiveValue<string>;
  renderCard: ({ item, index }: { item: T; index: number }) => ReactNode;
  /**
   * Extra readiness gate (e.g. `usePromotion().isFetched`). Holds the carousel at `opacity: 0`
   * until true so a conditionally prepended first slide cannot cause CLS once it resolves.
   */
  isReady?: boolean;
  /** Fires once per slide when the carousel is visible and in the viewport. Omit to skip view tracking. */
  onSlideView?: ({ item, index }: { item: T; index: number }) => void;
}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewedSlideRef = useRef<Record<number, boolean>>({});
  const isClientReady = useClientRender();
  // Mirror the catalog hero: hold hidden until the promo settles, then remount so the carousel
  // starts on slide 0 (the prepended flash slide) instead of wherever hidden autoplay drifted.
  const isCarouselVisible = isClientReady && isReady;
  const { onDestroy: onSwiperDestroy, onSwiper } = useDelayedSwiperAutoplay({
    isEnabled: isCarouselVisible && !isAutoplayPaused,
    isInViewport: isIntersecting,
    requiresViewport: autoplayStartOnViewport,
    startDelayMs: autoplayStartDelayMs,
  });
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const { i18n, isRtl } = useI18n();
  const cards = useMemo(() => {
    return items.map((item, index) => {
      return <SwiperSlide key={item.key}>{renderCard({ item, index })}</SwiperSlide>;
    });
  }, [items, renderCard]);
  const handleSlideView = useCallback(
    (realIndex: number) => {
      // Don't record views while the carousel is still held hidden (opacity: 0) waiting on the
      // promo gate. Autoplay would otherwise fire `onRealIndexChange` for the pre-flash slide and
      // poison `viewedSlideRef`, suppressing the real flash-slide view once the promo prepends a
      // slide and the swiper remounts.
      if (!isCarouselVisible || !onSlideView) {
        return;
      }
      const hasFiredEvent = viewedSlideRef.current[realIndex];
      const item = items[realIndex];
      if (isIntersecting && !hasFiredEvent && item) {
        viewedSlideRef.current[realIndex] = true;
        onSlideView({ item, index: realIndex });
      }
    },
    [items, isIntersecting, isCarouselVisible, onSlideView]
  );

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Emit the initial view for slide 0 once the carousel is visible (promo settled) and in view.
  // Swiper doesn't fire `onRealIndexChange` for the initial slide, so without this a prepended
  // flash slide's view would never be tracked. `handleSlideView` re-runs the effect when
  // intersection changes (covering the below-the-fold case) and dedupes via `viewedSlideRef`.
  useEffect(() => {
    if (!isCarouselVisible) {
      return;
    }
    handleSlideView(0);
  }, [handleSlideView, isCarouselVisible]);

  return (
    <Box ref={containerRef} position='relative'>
      <Box
        isolation='isolate'
        minHeight={minHeight}
        position='relative'
        sx={{
          '.swiper': {
            opacity: isCarouselVisible ? 1 : 0,
            height: isCarouselVisible ? undefined : '0px',
            transition: 'opacity 0.2s ease',
            paddingBottom: '48px',
          },
          '.swiper-slide': {
            height: 'auto',
            display: 'flex',
          },
          '.swiper-slide > *': {
            width: '100%',
          },
          // Fade stacks every slide in one box. Swiper sets `pointer-events: none` on inactive
          // slides, but descendants with `pointer-events: auto` (card CTAs) still receive clicks.
          '.swiper-slide:not(.swiper-slide-active) *': {
            pointerEvents: 'none !important',
          },
          '.swiper-pagination': {
            height: '30px',
          },
          '.swiper-pagination-bullet': {
            backgroundColor: 'blue.300',
            width: '16px',
            borderRadius: '8px',
            height: '4px',
            opacity: 0.33,
            transition: 'all 0.3s ease',
          },
          '.swiper-pagination-bullet-active': {
            backgroundColor: 'blue.300',
            opacity: 1,
            width: '56px',
            borderRadius: '8px',
            height: '4px',
          },
          // Note: this is a weird hack to make the swiper slide widths responsive.
          '.swiper-wrapper': {
            width: '1px',
          },
        }}
      >
        <Swiper
          key={isCarouselVisible ? 'ready' : 'pending'}
          direction='horizontal'
          effect='fade'
          longSwipes={false}
          modules={[A11y, Mousewheel, EffectFade, Autoplay, Navigation, Pagination]}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
            stopOnLastSlide: false,
            waitForTransition: true,
          }}
          fadeEffect={{
            crossFade: true,
          }}
          mousewheel={{
            forceToAxis: true,
            releaseOnEdges: true,
            thresholdDelta: 10,
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          pagination={{
            clickable: true,
          }}
          loop
          onDestroy={onSwiperDestroy}
          onSwiper={onSwiper}
          onBeforeInit={(swiper) => {
            swiper.changeLanguageDirection(i18n.dir());
          }}
          onRealIndexChange={(swiper) => {
            handleSlideView(swiper.realIndex);
          }}
        >
          {isClientReady ? cards : cards.slice(0, 1)}
        </Swiper>
      </Box>
      <IconButton
        ref={prevRef}
        aria-label='Previous'
        color='grey.500'
        icon={isRtl ? <ChevronRightIcon height='36px' width='36px' /> : <ChevronLeftIcon height='36px' width='36px' />}
        isDisabled={!isClientReady}
        left='-64px'
        opacity={0.5}
        position='absolute'
        size='md'
        top='calc(50% - 48px)'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'inline-flex',
        }}
      />
      <IconButton
        ref={nextRef}
        aria-label='Next'
        color='grey.500'
        icon={isRtl ? <ChevronLeftIcon height='36px' width='36px' /> : <ChevronRightIcon height='36px' width='36px' />}
        isDisabled={!isClientReady}
        opacity={0.5}
        position='absolute'
        right='-64px'
        size='md'
        top='calc(50% - 48px)'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'inline-flex',
        }}
      />
    </Box>
  );
}
